// ---------------------------------------------------------------------------
// FocusSessionProvider — o estado da sessão de Foco no nível do app.
// Tipos, contextos e hooks moram em `focusSession.ts` (o `eslint.config.js`
// proíbe misturar componente e helper em arquivo novo).
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useUserBooksContext } from "../../../contexts/UserBooksContext";
import {
  focusSessionFromStartAck,
  getActiveFocusSession,
  registerFocusLiveActivityPush,
  startFocusSession,
  type FocusSession,
} from "../../../services/firebase/focusService";
import { track } from "../../../lib/telemetry";
import { fetchUserBookByAnyId, type UserBook } from "../../../services/firebase/booksService";
import { fetchFcmTransportToken } from "../../../lib/firebase/messaging";
import {
  installFocusLiveActivityPushBridge,
  syncFocusLiveActivity,
} from "../lib/focusLiveActivity";
import { syncFocusNotification } from "../lib/focusNotification";
import { hasTargetElapsed } from "../lib/focusElapsed";
import { FOCUS_RECOVER_READ_TIMEOUT_MS, withTimeout } from "../lib/withTimeout";
import {
  forgetFocusPresentation,
  readFocusPresentationExpanded,
} from "../lib/focusPresentation";
import {
  forgetFocusStartIntent,
  intentMatchesSession,
  isDefinitiveStartRejection,
  readFocusStartIntent,
  resolveStartIntentReplay,
} from "../lib/focusStartIntent";
import {
  installFocusCommandBridge,
  nativeOnFocusDeepLink,
} from "../lib/focusDeepLinkNative";
import type { FocusDeepLinkCommand } from "../lib/focusDeepLink";
import { isNativeApp } from "../../../lib/platform/native";
import type { FocusTarget } from "../hooks/useFocusTimer";
import {
  FocusSessionContext,
  FocusSessionHostContext,
  recoveredFocusRequest,
  resolveFinishedBook,
  type FocusActionRequest,
  type FocusCloseOutcome,
  type FocusSessionContextValue,
  type FocusSessionHostValue,
  type FocusSessionRequest,
  type FocusStartRequest,
} from "./focusSession";

/** Ms de um `Timestamp` do Firestore, ou `null` se o campo não for legível. */
function msOf(v: { toMillis?: () => number } | null | undefined): number | null {
  const ms = typeof v?.toMillis === "function" ? v.toMillis() : null;
  return typeof ms === "number" && Number.isFinite(ms) ? ms : null;
}

/**
 * A sessão recuperada já venceu? Sem timing legível devolve `false` — o lado
 * seguro, porque abrir expandido é o comportamento excepcional.
 *
 * Sessão com `status: "rewarded"` é o caso terminal e nem chega a ser medida:
 * ela **acabou** (a fase 1 creditou e carimbou `rewardedAt`), e o serviço só a
 * devolve quando as páginas ficaram pendentes. Abri-la minimizada esconderia o
 * único motivo de ela ter sido recuperada.
 */
function sessaoVenceu(active: {
  status?: string;
  targetDurationSec?: number;
  startedAt?: { toMillis?: () => number } | null;
  pausedAt?: { toMillis?: () => number } | null;
  pausedTotalSec?: number;
}): boolean {
  if (active.status === "rewarded") return true;
  const startedAtMs = msOf(active.startedAt);
  if (startedAtMs == null) return false;
  return hasTargetElapsed(
    {
      targetDurationSec: active.targetDurationSec ?? 0,
      startedAtMs,
      pausedAtMs: msOf(active.pausedAt),
      pausedTotalSec: active.pausedTotalSec ?? 0,
    },
    Date.now(),
  );
}

/**
 * Tentativas da recuperação depois de uma FALHA de leitura (BUG-222).
 *
 * Leitura que falha sempre (rules, rede morta) não pode virar laço num
 * provider montado no nível do app — mas uma falha transitória também não pode
 * esconder a sessão viva até alguém matar o processo, que era o de antes.
 */
const MAX_RECOVER_ATTEMPTS = 3;
/** Espera da 1ª retentativa. Cresce linearmente a cada falha. */
const RECOVER_RETRY_BASE_MS = 1500;

export function FocusSessionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { allBooks, isLoading: shelfLoading } = useUserBooksContext();

  const [request, setRequest] = useState<FocusSessionRequest | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [expandSignal, setExpandSignal] = useState(0);
  const [endedSignal, setEndedSignal] = useState(0);

  /**
   * Claim da recuperação. É a IDENTIDADE da conta, e não um booleano com
   * `cancelled` na limpeza — essa forma se destrói sozinha no `StrictMode`
   * (efeito → limpeza → efeito: a 2ª execução vê o claim tomado e não pede
   * nada, e a promise da 1ª é descartada pelo `cancelled`; ninguém devolve o
   * claim e a leitura nunca assenta). Foi o BUG-141, e a cura registrada lá é
   * exatamente esta: reexecutar o efeito vira inofensivo por construção.
   */
  const recoveredForRef = useRef<string | null>(null);

  /**
   * Retentativa da recuperação (BUG-222). O claim acima é tomado ANTES da
   * leitura e não era devolvido no `catch`: uma falha transitória escondia a
   * sessão VIVA pelo resto desta carga do app.
   */
  const recoverAttemptsRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const [recoverRetry, setRecoverRetry] = useState(0);
  useEffect(
    () => () => {
      if (retryTimerRef.current != null) window.clearTimeout(retryTimerRef.current);
    },
    [],
  );

  /**
   * Exemplar buscado DIRETO no Firestore (BUG-215) — o caminho de quem a janela
   * da estante (400, por `updatedAt`) não alcança. `book: null` significa
   * "buscou e não achou", e é por isso que o resultado é guardado com o
   * `bookId`: sem ele a busca repetiria a cada render da estante.
   */
  const [fetchedBook, setFetchedBook] = useState<{ bookId: string; book: UserBook | null } | null>(
    null,
  );
  const fetchedForRef = useRef<string | null>(null);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      recoveredForRef.current = null;
      fetchedForRef.current = null;
      recoverAttemptsRef.current = 0;
      return;
    }
    if (recoveredForRef.current === uid) return;
    // A estante é o que resolve o exemplar da sessão — sem ela, a recuperação
    // acharia a sessão e não teria o que mostrar no drawer.
    if (shelfLoading) return;
    recoveredForRef.current = uid;

    void (async () => {
      try {
        /*
         * Com PRAZO (BUG-234): o transporte reconectando depois do bloqueio
         * não rejeita, pendura. A retentativa abaixo só reagia a rejeição —
         * uma leitura pendurada deixava o app sem o modal de uma sessão viva
         * até matar o processo. O prazo vira rejeição comum, e a retentativa
         * já existente cuida do resto.
         */
        let active: FocusSession | null = await withTimeout(
          getActiveFocusSession(uid),
          FOCUS_RECOVER_READ_TIMEOUT_MS,
          "recuperação do Foco",
        );
        // Commit só se ainda é a mesma conta (ver o claim acima).
        if (recoveredForRef.current !== uid) return;

        /*
         * ── A intenção de iniciar que morreu em voo (BUG-275) ──────────────
         *
         * No app das lojas, iniciar o Foco na Home e bloquear a tela em
         * seguida é iniciar e ter a WebView RECRIADA por baixo (BUG-197) —
         * este boot. Se o `startFocusSession` não tinha chegado ao servidor,
         * a leitura acima devolve `null` e o Foco sumia: a pessoa voltava para
         * a Home sem sessão, sem crédito e sem uma linha explicando. O
         * `useFocusTimer` persiste a intenção ANTES da rede; aqui ela é
         * repetida com o instante original, e o servidor recua a sessão até
         * ele (com teto). Achou sessão no servidor? A intenção cumpriu o
         * papel — quer seja a dela (o pedido chegou) ou outra (`reuse`).
         *
         * Falha de transporte no replay cai no `catch` de baixo, que é a
         * retentativa da recuperação inteira; recusa definitiva apaga a
         * intenção, porque nenhum boot vai transformá-la em sessão.
         */
        const intent = readFocusStartIntent();
        if (intent) {
          if (active) {
            forgetFocusStartIntent();
          } else if (resolveStartIntentReplay(intent, Date.now()) === "replay") {
            let ack;
            try {
              ack = await withTimeout(
                startFocusSession(
                  uid,
                  intent.bookId,
                  intent.durationSec,
                  intent.targetPages,
                  intent.targetPercent,
                  intent.snapshot ?? undefined,
                  intent.localStartMs,
                ),
                FOCUS_RECOVER_READ_TIMEOUT_MS,
                "replay do start do Foco",
              );
            } catch (err) {
              if (recoveredForRef.current !== uid) return;
              if (!isDefinitiveStartRejection(err)) throw err;
              forgetFocusStartIntent();
            }
            if (recoveredForRef.current !== uid) return;
            if (ack) {
              active = focusSessionFromStartAck(uid, ack, intent.snapshot);
              forgetFocusStartIntent();
              track("focus_start_replayed_on_boot", {
                sessionId: ack.sessionId,
                ageSec: Math.round((Date.now() - intent.localStartMs) / 1000),
              });
            }
          } else {
            forgetFocusStartIntent();
          }
        }

        if (!active) {
          /**
           * Nenhuma sessão no servidor — a única resposta CONFIÁVEL de "não há
           * o que mostrar", e é ela que manda varrer as Live Activities de um
           * processo que morreu. Sem o mount global, este caminho não existia:
           * o efeito que reconcilia a Ilha mora no modal, e o modal só montava
           * quando já havia sessão.
           *
           * O aviso LOCAL também (BUG-234): sessão cancelada/concluída em OUTRO
           * aparelho deixava agendado neste o "registre as páginas" de uma
           * sessão que não existe mais — o agendamento vive no SO, e o módulo
           * só cancelava o que este processo tinha agendado.
           */
          void syncFocusLiveActivity(null);
          void syncFocusNotification(null);
          // Não há sessão: a apresentação lembrada não pertence a ninguém.
          forgetFocusPresentation();
          return;
        }

        /*
          Minimizado é o certo para sessão EM ANDAMENTO — quem trocou de aba não
          pediu a prisão de volta na cara. Para sessão que já VENCEU é o oposto,
          e era o defeito: o `useFocusTimer` detecta o vencimento e entra em
          `wrap_up` corretamente, mas com o drawer fechado esse wrap-up nasce
          dentro de uma tarja de 116px no rodapé. A pessoa via uma faixa, não o
          pedido das páginas — e 20,9% dos pomodoros terminam sem página
          declarada (medido em 03/09).

          Sessão pausada e sessão livre nunca "vencem" (`computeDueMs` devolve
          `null` nos dois casos), então elas seguem abrindo minimizadas.
        */
        /* DEFENSIVO por decisão: doc sem `startedAt` (ou com o campo em forma
          inesperada) não pode DERRUBAR a recuperação. Ela é conveniência — o
          `catch` abaixo devolveria o app de hoje, mas o custo seria a sessão
          viva sumindo da tela por causa de um campo. Sem timing legível a
          resposta é "não venceu", que é o comportamento de antes desta fatia.
        */
        const venceu = sessaoVenceu(active);

        /**
         * 🔴 A apresentação SOBREVIVE ao boot (BUG-222).
         *
         * No app das lojas, voltar do bloqueio na Home ou no Perfil recria a
         * WebView (a cura preventiva do BUG-197) — um boot de JS inteiro, com
         * o React remontando por baixo de quem estava lendo. Sem isto, quem
         * estava em TELA CHEIA caía no drawer de 116px, que é o "saiu do modo
         * foco" do relato: ela não pediu nada, o app é que esqueceu.
         *
         * ⚠️ O default segue MINIMIZADA (invariante 17 ④) — o que muda é que
         * a apresentação deixa de ser descartada. Quem minimizou tem o
         * marcador em `false` e continua no drawer, que é exatamente o caso
         * que aquele invariante foi escrito para proteger.
         */
        /*
         * A sessão nascida de uma intenção (BUG-275) abre EXPANDIDA: o
         * marcador de apresentação pula o id pendente (invariante 48 ②), então
         * a sessão cujo ack nunca chegou a este processo não tem marcador —
         * e a pessoa acabou de tocar "Iniciar". A folga é a do ack do start.
         */
        const estavaExpandida =
          readFocusPresentationExpanded(active.id) ||
          (intent != null && intentMatchesSession(intent, msOf(active.startedAt)));
        const abreMinimizada = !venceu && !estavaExpandida;

        setRequest((prev) => {
          // Quem já começou uma sessão nesta tela ganha da recuperação.
          if (prev) return prev;
          /*
            ⚠️ A `allBooks` desta closure é a do commit em que o claim foi
            tomado — e no cold start ela é VAZIA (BUG-215): o `isLoading` da
            estante ainda diz `false` do estado "sem conta", então o exemplar
            NÃO se resolve aqui na maioria das vezes. O que se resolve aqui é o
            que o doc da sessão carrega (título, capa, formato); o exemplar
            chega pelo `effectiveRequest`, que re-resolve a cada estante nova.
            Sem exemplar E sem foto (sessão anterior a esta fatia, livro
            removido da estante): o drawer ainda precisa de um nome, e o timer
            só precisa do id.
          */
          return {
            ...recoveredFocusRequest(active, allBooks, null),
            target: null,
            minimized: abreMinimizada,
            recovered: true,
            // O timer adota ESTE doc em vez de reler (BUG-228).
            session: active,
          };
        });
        setMinimized(abreMinimizada);
      } catch {
        /**
         * 🔴 O claim é DEVOLVIDO aqui (BUG-222) — e não era.
         *
         * Ele é tomado ANTES da leitura, então mantê-lo no `catch` fazia UMA
         * falha transitória esconder a sessão VIVA pelo resto desta carga do
         * app: sem retry, sem nada na tela, e sem outra saída além de matar o
         * processo. Foi literalmente o que a usuária do relato fez ("fechou e
         * abriu dnv e deu certo").
         *
         * E o instante logo depois da recriação da WebView (BUG-197) é
         * exatamente quando o Firestore está se reconectando — ou seja, quando
         * essa falha é MAIS provável, não menos.
         *
         * Recuperação segue sendo conveniência: falhar as três vezes devolve o
         * app de hoje, nunca um app quebrado.
         */
        if (recoveredForRef.current !== uid) return;
        /**
         * ⚠️ Esgotadas as tentativas, o claim FICA TOMADO — e essa ordem é o
         * ponto. Devolvê-lo aqui deixaria o efeito rearmar a cada render (a
         * `allBooks` chega com identidade nova a cada snapshot da estante), ou
         * seja um laço de leituras contra um servidor que já disse não.
         */
        if (recoverAttemptsRef.current >= MAX_RECOVER_ATTEMPTS) return;
        recoverAttemptsRef.current += 1;
        retryTimerRef.current = window.setTimeout(() => {
          /**
           * O claim é devolvido SÓ AQUI. Enquanto ele estiver tomado ele age
           * como trava de "leitura em voo": nenhum re-render dispara uma
           * segunda leitura no meio do intervalo. E se a conta trocou nesse
           * meio-tempo, o claim é de outro dono e não se mexe nele.
           */
          if (recoveredForRef.current === uid) recoveredForRef.current = null;
          setRecoverRetry((n) => n + 1);
        }, RECOVER_RETRY_BASE_MS * recoverAttemptsRef.current);
      }
    })();
  }, [user?.uid, shelfLoading, allBooks, recoverRetry]);

  /**
   * Controles da Live Activity (nível 3, opção (c) de 23/08).
   *
   * O provider CAPTURA a URL porque é ele que está montado no nível do app —
   * quando a pessoa toca "Pausar" na tela de bloqueio, o app pode estar
   * abrindo do zero e o modal ainda não existe. Quem EXECUTA é o host, que
   * tem o `useFocusTimer`.
   *
   * ⚠️ O pedido fica guardado até alguém consumir: a URL chega ANTES de a
   * sessão ser recuperada num cold start, e descartá-la ali seria perder
   * exatamente o toque que abriu o app.
   *
   * ⚠️ São DUAS portas, e a segunda existe porque a primeira morre (F8). O
   * `appUrlOpen` é entregue pelo bridge do Capacitor — que a recriação
   * preventiva do BUG-197 destrói em Home e Perfil, junto com o evento retido
   * (`retainedEventArguments` é de INSTÂNCIA no plugin, não do processo). Nessas
   * rotas o comando volta pelo `AppDelegate`, que o guardou em `UserDefaults`
   * antes de repassar. As duas terminam no MESMO `setActionRequest` de
   * propósito: o consumidor não precisa saber por onde o toque entrou, e a
   * dedupe de reentrega mora na ponte.
   */
  const [actionRequest, setActionRequest] = useState<FocusActionRequest | null>(null);

  /**
   * A ponte do PUSH DE CONCLUSÃO da Live Activity (blueprint
   * `focus-live-activity-push.md`). O módulo da Ilha não importa Firebase; quem
   * sabe chamar a callable e pedir o token do FCM é este provider, que está
   * montado no nível do app — o token da activity chega segundos depois do
   * start, e pode chegar de novo no relaunch (rotação), quando modal nenhum
   * existe ainda. Mostrador, nunca economia: falha aqui vira warn no módulo.
   */
  useEffect(() => {
    const uid = user?.uid;
    if (!isNativeApp() || !uid) {
      installFocusLiveActivityPushBridge(null);
      return;
    }
    installFocusLiveActivityPushBridge({
      register: (input) => registerFocusLiveActivityPush(uid, input),
      // Transporte, sem pedir permissão de alertas — ver `fetchFcmTransportToken`.
      getFcmToken: () => fetchFcmTransportToken(),
    });
    return () => installFocusLiveActivityPushBridge(null);
  }, [user?.uid]);

  useEffect(() => {
    if (!isNativeApp()) return;
    let ativo = true;
    let remover: (() => void) | null = null;
    const receber = (command: FocusDeepLinkCommand): void => {
      setActionRequest((prev) => ({ ...command, seq: (prev?.seq ?? 0) + 1 }));
    };
    void nativeOnFocusDeepLink(receber).then((off) => {
      if (ativo) remover = off;
      else off();
    });
    const desinstalar = installFocusCommandBridge(receber);
    return () => {
      ativo = false;
      remover?.();
      desinstalar();
    };
  }, []);

  /**
   * O pedido que os consumidores veem (BUG-215).
   *
   * Para sessão RECUPERADA sem exemplar, re-resolve contra a estante ATUAL a
   * cada render — é derivado, não sincronizado por efeito, então não há
   * `setState` a esquecer nem estado a divergir. Quando o exemplar aparece
   * (a estante chegou, ou a busca direta achou), o pedido passa a ser o dele:
   * título e capa da estante, progresso e total para o wrap-up, e o `book`
   * que habilita a nota da sessão. `target`/`minimized`/`recovered` são da
   * SESSÃO, não do livro, e sobrevivem à troca.
   *
   * Sessão iniciada nesta carga (`recovered` ausente) e sessão recuperada já
   * com exemplar passam intocadas — o pedido é o mesmo objeto, então nenhum
   * consumidor re-renderiza à toa.
   */
  const effectiveRequest = useMemo<FocusSessionRequest | null>(() => {
    if (!request?.recovered || request.book) return request;
    const fetched = fetchedBook?.bookId === request.bookId ? fetchedBook.book : null;
    const resolved = recoveredFocusRequest(
      {
        bookId: request.bookId,
        bookTitle: request.title,
        bookCoverUrl: request.coverUrl,
        bookFormat: request.format,
      },
      allBooks,
      fetched,
    );
    if (!resolved.book) return request;
    return {
      ...resolved,
      target: request.target,
      minimized: request.minimized,
      recovered: true,
      // A sessão é da SESSÃO, não do livro: sobrevive à troca (BUG-228).
      session: request.session ?? null,
    };
  }, [request, allBooks, fetchedBook]);

  /**
   * Busca DIRETA do exemplar — só quando a estante CHEGOU e não o tem.
   *
   * Duas guardas que não são otimização, são semântica: ① estante vazia não
   * dispara nada (o livro não pode estar numa estante vazia — e no cold start a
   * estante está vazia porque ainda não carregou, não porque não tem o livro:
   * buscar aqui seria pagar leitura pelo que o `effectiveRequest` vai resolver
   * de graça no commit seguinte); ② uma busca por `bookId`, guardada no ref,
   * inclusive quando ela devolve `null` — sessão de livro removido da estante
   * não pode custar uma leitura por render.
   *
   * O `setState` mora na promise, nunca no corpo do efeito.
   */
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || !effectiveRequest?.recovered || effectiveRequest.book) return;
    if (shelfLoading || allBooks.length === 0) return;
    const { bookId } = effectiveRequest;
    if (fetchedForRef.current === bookId) return;
    fetchedForRef.current = bookId;
    // `Promise.resolve().then(...)`: um `throw` síncrono da busca cai no mesmo
    // `catch` que a rejeição — a recuperação é conveniência e não pode derrubar
    // o provider, que é o dono da sessão no app inteiro.
    void Promise.resolve()
      .then(() => fetchUserBookByAnyId(uid, bookId))
      .then((book) => {
        if (fetchedForRef.current === bookId) setFetchedBook({ bookId, book: book ?? null });
      })
      .catch(() => {
        // Busca é conveniência: sem ela o drawer segue com a foto do doc.
      });
  }, [user?.uid, effectiveRequest, shelfLoading, allBooks]);

  const startFocus = useCallback((input: FocusStartRequest, target: FocusTarget) => {
    setRequest({ ...input, target, minimized: false });
    setMinimized(false);
  }, []);

  const expandFocus = useCallback(() => setExpandSignal((s) => s + 1), []);

  /**
   * Livro concluído pelo Foco, à espera da tela de conclusão (BUG-304). O
   * `closeFocus` lê pedido e estante por ref: ele é estável de propósito (o
   * modal o recebe como `onClose`), e com os dois nas deps mudaria a cada
   * snapshot da estante.
   */
  const [finishedBook, setFinishedBook] = useState<UserBook | null>(null);
  const closeSourceRef = useRef({ request: effectiveRequest, shelf: allBooks });
  useEffect(() => {
    closeSourceRef.current = { request: effectiveRequest, shelf: allBooks };
  }, [effectiveRequest, allBooks]);
  const dismissFinishedBook = useCallback(() => setFinishedBook(null), []);

  const closeFocus = useCallback((outcome?: FocusCloseOutcome) => {
    const { request: closing, shelf } = closeSourceRef.current;
    const finished = resolveFinishedBook(outcome, closing, shelf);
    if (finished) setFinishedBook(finished);
    // A sessão acabou para este app: a apresentação lembrada morre com ela —
    // e a intenção de iniciar também (BUG-275).
    forgetFocusPresentation();
    forgetFocusStartIntent();
    setRequest(null);
    setFetchedBook(null);
    fetchedForRef.current = null;
    setMinimized(false);
    setEndedSignal((s) => s + 1);
  }, []);

  const value = useMemo<FocusSessionContextValue>(
    () => ({
      request: effectiveRequest,
      isActive: effectiveRequest !== null,
      minimized,
      expandSignal,
      endedSignal,
      startFocus,
      expandFocus,
    }),
    [effectiveRequest, minimized, expandSignal, endedSignal, startFocus, expandFocus],
  );

  const hostValue = useMemo<FocusSessionHostValue>(
    () => ({
      request: effectiveRequest,
      expandSignal,
      actionRequest,
      setMinimized,
      closeFocus,
      finishedBook,
      dismissFinishedBook,
    }),
    [effectiveRequest, expandSignal, actionRequest, closeFocus, finishedBook, dismissFinishedBook],
  );

  return (
    <FocusSessionContext.Provider value={value}>
      <FocusSessionHostContext.Provider value={hostValue}>
        {children}
      </FocusSessionHostContext.Provider>
    </FocusSessionContext.Provider>
  );
}

